(() => {
  'use strict';
  const THRESHOLD = 55000;
  const GIFT_PRODUCT = 8671353208988;
  const validMoney = amount => Number.isSafeInteger(amount) && amount >= 0;
  const isGift = item => Number(item.product_id) === GIFT_PRODUCT && item.properties?._safira_gift === 'blue550';
  const unknown = reason => ({ status: 'unknown', amount: null, currency: 'EGP', reason });

  function calculateNetPaid(cart, authoritative) {
    if (!cart || cart.currency !== 'EGP' || !Array.isArray(cart.items)) return unknown('cart-unavailable');
    if (authoritative !== undefined && authoritative !== null) {
      if (authoritative.currency === 'EGP' && authoritative.afterDiscounts === true && authoritative.excludesGift === true && validMoney(authoritative.amount) && validMoney(cart.total_price) && authoritative.amount <= cart.total_price) {
        return { status: 'known', amount: authoritative.amount, currency: 'EGP' };
      }
      return unknown('invalid-authoritative-net');
    }
    if (!validMoney(cart.total_price) || cart.items.some(item => !validMoney(item.final_line_price))) return unknown('missing-totals');
    const sum = cart.items.reduce((total, item) => total + item.final_line_price, 0);
    if (!validMoney(sum) || cart.total_price > sum) return unknown('inconsistent-totals');
    const gifts = cart.items.filter(isGift);
    if (!gifts.length || gifts.every(item => item.final_line_price === 0)) {
      return { status: 'known', amount: cart.total_price, currency: 'EGP' };
    }
    if (sum === cart.total_price) {
      return { status: 'known', amount: Math.max(0, cart.total_price - gifts.reduce((total, item) => total + item.final_line_price, 0)), currency: 'EGP' };
    }
    const allocated = cart.items.map(item => {
      if (!validMoney(item.original_line_price) || !Array.isArray(item.discount_allocations) || item.discount_allocations.some(part => !validMoney(part.amount))) return null;
      const value = item.original_line_price - item.discount_allocations.reduce((total, part) => total + part.amount, 0);
      return validMoney(value) ? value : null;
    });
    if (allocated.every(value => value !== null) && allocated.reduce((total, value) => total + value, 0) === cart.total_price) {
      return { status: 'known', amount: allocated.reduce((total, value, index) => total + (isGift(cart.items[index]) ? 0 : value), 0), currency: 'EGP' };
    }
    return unknown('gift-discount-allocation-unavailable');
  }

  function calculateProgress(net) {
    if (net?.status !== 'known' || net.currency !== 'EGP' || !validMoney(net.amount)) return null;
    return { remaining: Math.max(0, THRESHOLD - net.amount), fraction: Math.min(1, net.amount / THRESHOLD), qualified: net.amount >= THRESHOLD };
  }
  function formatRemaining(amount, locale) {
    return new Intl.NumberFormat(String(locale).startsWith('ar') ? 'ar-EG' : 'en-EG', { style: 'currency', currency: 'EGP', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(amount / 100);
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { calculateNetPaid, calculateProgress, formatRemaining, THRESHOLD, GIFT_PRODUCT };
  if (typeof window === 'undefined' || typeof document === 'undefined' || window.SafiraPostOrderGift) return;
  const initial = window.SafiraPostOrderGiftConfig;
  const adapter = typeof initial?.getNetPaid === 'function' ? initial.getNetPaid : null;
  let root, state = unknown('not-loaded');
  let pending = null, refreshAgain = false, timer = null;
  let disposed = false;
  const thresholdFrames = new WeakMap();
  function cancelThresholdFrames(element) {
    const pendingFrame = thresholdFrames.get(element);
    if (pendingFrame) window.cancelAnimationFrame?.(pendingFrame.id);
    thresholdFrames.delete(element);
    return Boolean(pendingFrame);
  }
  function setThresholdScale(element, fraction, firstShow) {
    const wasPending = cancelThresholdFrames(element);
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if ((!firstShow && !wasPending) || !window.requestAnimationFrame || reducedMotion) {
      element.style.setProperty('--sf-threshold-scale', String(fraction));
      return;
    }
    element.style.setProperty('--sf-threshold-scale', '0');
    const frame = { id: null };
    thresholdFrames.set(element, frame);
    const current = () => thresholdFrames.get(element) === frame && !disposed && !element.hidden && element.isConnected;
    frame.id = window.requestAnimationFrame(() => {
      if (!current()) { if (thresholdFrames.get(element) === frame) thresholdFrames.delete(element); return; }
      frame.id = window.requestAnimationFrame(() => {
        if (current()) element.style.setProperty('--sf-threshold-scale', String(fraction));
        if (thresholdFrames.get(element) === frame) thresholdFrames.delete(element);
      });
    });
  }
  function updateThresholds() {
    const progress = calculateProgress(state);
    document.querySelectorAll('[data-sf-threshold]').forEach(element => {
      const label = element.querySelector('[data-sf-threshold-status]');
      const track = element.querySelector('[data-sf-threshold-track]');
      let copy;
      try { copy = JSON.parse(element.querySelector('[data-sf-threshold-text]').textContent); } catch (_) {}
      if (!progress || !copy || !label || !track) {
        cancelThresholdFrames(element);
        element.hidden = true;
        if (label) label.textContent = '';
        if (track) { track.removeAttribute('aria-valuenow'); track.removeAttribute('aria-valuetext'); }
        return;
      }
      const message = progress.qualified ? copy.unlocked : copy.remaining.replace('{amount}', formatRemaining(progress.remaining, element.dataset.sfThresholdLocale));
      if (label.textContent !== message) label.textContent = message;
      track.setAttribute('aria-valuenow', String(Math.min(THRESHOLD, state.amount)));
      track.setAttribute('aria-valuetext', message);
      const firstShow = element.hidden;
      element.dataset.qualified = String(progress.qualified);
      element.hidden = false;
      setThresholdScale(element, progress.fraction, firstShow);
    });
  }
  function updateStatus(next) {
    state = next;
    updateThresholds();
  }
  async function refresh() {
    if (!root || disposed) return;
    if (pending) { refreshAgain = true; return pending; }
    pending = (async () => {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 10000);
      try {
        const route = window.Shopify?.routes?.root || '/';
        const url = new URL(route.replace(/\/?$/, '/') + 'cart.js', window.location.origin);
        if (url.origin !== window.location.origin) throw new Error('invalid-route');
        const response = await window.fetch(url.href, { method: 'GET', credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw new Error('cart-unavailable');
        const cart = await response.json();
        const authoritative = adapter ? await adapter(cart) : undefined;
        if (!disposed) updateStatus(calculateNetPaid(cart, authoritative));
      } catch (_) {
        if (!disposed) updateStatus(unknown('cart-unavailable'));
      } finally { window.clearTimeout(timeout); }
    })().finally(() => {
      pending = null;
      if (refreshAgain && !disposed) { refreshAgain = false; schedule(); }
    });
    return pending;
  }
  function schedule() {
    if (disposed || document.hidden) return;
    window.clearTimeout(timer);
    timer = window.setTimeout(refresh, 160);
  }
  function mount() {
    root = document.documentElement;
    for (const name of ['cart:updated', 'cart:update', 'cart:change', 'cart:refresh', 'cart:discount-update', 'discount:update', 'shopify:section:load']) document.addEventListener(name, schedule, true);
    if (window.MutationObserver) {
      new window.MutationObserver(records => {
        const inserted = records.some(record => Array.from(record.addedNodes).some(node => node.nodeType === 1 && (node.matches?.('[data-sf-threshold]') || node.querySelector?.('[data-sf-threshold]'))));
        if (inserted && !disposed) { updateThresholds(); schedule(); }
      }).observe(document.body, { childList: true, subtree: true });
    }
    window.addEventListener('pageshow', event => { disposed = false; if (event.persisted) { schedule(); } });
    window.addEventListener('pagehide', () => { disposed = true; window.clearTimeout(timer); });
    document.addEventListener('visibilitychange', () => { if (!document.hidden) schedule(); });
    if (window.PerformanceObserver) {
      try {
        new window.PerformanceObserver(list => {
          if (list.getEntries().some(entry => /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/.test(entry.name))) schedule();
        }).observe({ type: 'resource', buffered: false });
      } catch (_) {}
    }
    refresh();
  }
  window.SafiraPostOrderGift = Object.freeze({ mode: 'shipping-only', refresh, getState: () => Object.freeze({ ...state }), calculateNetPaid });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true });
  else mount();
})();



